import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const genId = () =>
    (crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)).replaceAll("-", "");

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

async function retryUntilOk(
    url,
    onOk,
    { baseDelay = 300, maxDelay = 2000 } = {},
    controller,
) {
    let delay = baseDelay, tries = 0;
    console.log('[DEBUG useRtspToHls] retryUntilOk started for URL:', url);

    while (!controller.signal.aborted) {
        tries++;
        const fetchUrl = `${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}`;
        console.log(`[DEBUG useRtspToHls] Polling attempt ${tries} for playlist:`, fetchUrl);

        try {
            const res = await fetch(fetchUrl, {
                cache: "no-store",
                mode: "cors",
                signal: controller.signal,
            });

            console.log(`[DEBUG useRtspToHls] Poll response status (attempt ${tries}):`, res.status);

            if (res.ok) {
                const txt = await res.text();
                console.log(`[DEBUG useRtspToHls] Response text preview (attempt ${tries}):`, txt.substring(0, 100));

                if (txt.startsWith("#EXTM3U")) {
                    console.log(`[DEBUG useRtspToHls] Valid playlist found on attempt ${tries}!`);
                    await onOk();
                    console.log(`[DEBUG useRtspToHls] onOk callback completed`);
                    return;
                } else {
                    console.log(`[DEBUG useRtspToHls] Response is not a valid M3U8 playlist (attempt ${tries})`);
                }
            } else {
                console.log(`[DEBUG useRtspToHls] Response not OK (attempt ${tries}):`, res.status, res.statusText);
            }
        } catch (error) {
            console.log(`[DEBUG useRtspToHls] Fetch error on attempt ${tries}:`, error.message);
            /* ignore -> tiếp tục retry */
        }
        console.log(`[DEBUG useRtspToHls] Đợi playlist… thử lần ${tries}, delay: ${delay}ms`);
        await wait(delay);
        delay = Math.min(Math.floor(delay * 1.8), maxDelay);
    }

    console.log('[DEBUG useRtspToHls] retryUntilOk aborted or stopped');
}

export const useRtspToHls = ({ autoStart = true } = {}) => {
    const [apiBase, setApiBase] = useState("http://localhost:25699");
    const [rtsp, setRtsp] = useState("");
    const [forceH264, setForceH264] = useState(false);

    const [playlist, setPlaylist] = useState("");  // m3u8 hợp lệ
    const [busy, setBusy] = useState(false);
    const [busyMsg, setBusyMsg] = useState("");

    const sessionIdRef = useRef("");
    const retryCtrlRef = useRef(null);
    const startedRef = useRef(false);

    const build = useCallback(
        (url) => `${apiBase.replace(/\/+$/, "")}${url.startsWith("/") ? url : "/" + url}`,
        [apiBase]
    );

    const startStream = useCallback(async () => {
        console.log('[DEBUG useRtspToHls] startStream() called');
        console.log('[DEBUG useRtspToHls] RTSP value:', rtsp);

        if (!rtsp.trim()) {
            console.log('[DEBUG useRtspToHls] RTSP is empty, returning early');
            return;
        }

        console.log('[DEBUG useRtspToHls] Starting stream conversion process...');
        // Clear trước khi start
        setPlaylist("");
        startedRef.current = true;
        console.log('[DEBUG useRtspToHls] Cleared previous playlist, set startedRef to true');

        // Hủy retry cũ (nếu có) rồi tạo controller mới
        if (retryCtrlRef.current) {
            console.log('[DEBUG useRtspToHls] Aborting previous retry controller');
            retryCtrlRef.current.abort();
        }
        retryCtrlRef.current = new AbortController();
        console.log('[DEBUG useRtspToHls] Created new AbortController');

        // Tạo session id mới
        sessionIdRef.current = genId();
        console.log('[DEBUG useRtspToHls] Generated new session_id:', sessionIdRef.current);

        try {
            const apiUrl = build("/api/start");
            const requestBody = {
                rtsp: rtsp.trim(),
                session_id: sessionIdRef.current,
                h264: !!forceH264,
            };

            console.log('[DEBUG useRtspToHls] Sending start request:', {
                url: apiUrl,
                body: requestBody
            });

            const res = await fetch(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                mode: "cors",
                body: JSON.stringify(requestBody),
            });

            console.log('[DEBUG useRtspToHls] Start API response status:', res.status);

            if (!res.ok) {
                console.error('[DEBUG useRtspToHls] Start API failed with status:', res.status);
                throw new Error("start_failed");
            }

            const data = await res.json();
            const m3u8Url = data.playlist; // ví dụ: http://localhost:25699/hls/<sid>/index.m3u8
            console.log('[DEBUG useRtspToHls] Received m3u8 URL:', m3u8Url);
            console.log('[DEBUG useRtspToHls] Starting to poll for valid playlist...');

            // Poll tới khi manifest hợp lệ mới set vào player
            await retryUntilOk(
                m3u8Url,
                async () => {
                    console.log('[DEBUG useRtspToHls] Playlist is valid, setting playlist URL:', m3u8Url);
                    setPlaylist(m3u8Url);
                    console.log('[DEBUG useRtspToHls] Playlist set successfully, ready for player');
                },
                { baseDelay: 300, maxDelay: 2000 },
                retryCtrlRef.current,
            );
        } catch (e) {
            if (!startedRef.current) {
                console.log('[DEBUG useRtspToHls] Stream was stopped during start, ignoring error');
                return; // đã stop giữa chừng
            }
            console.error('[DEBUG useRtspToHls] Error in startStream:', e);
            console.log("Không khởi tạo được stream / lỗi mạng.");
        }
    }, [build, rtsp, forceH264]);

    const stopStream = useCallback(async () => {
        startedRef.current = false;

        // Hủy retry nếu đang chạy
        if (retryCtrlRef.current) {
            retryCtrlRef.current.abort();
            retryCtrlRef.current = null;
        }

        try {
            if (sessionIdRef.current) {
                await fetch(build("/api/stop"), {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    mode: "cors",
                    keepalive: true,
                    body: JSON.stringify({ session_id: sessionIdRef.current }),
                });
            }
        } catch {
            /* ignore */
        } finally {
            setPlaylist("");
            setBusy(false);
            setBusyMsg("");
        }
    }, [build]);

    // Tự start khi rtsp thay đổi (nếu autoStart=true)
    useEffect(() => {
        console.log('[DEBUG useRtspToHls] RTSP path changed:', {
            rtsp: rtsp,
            autoStart: autoStart,
            isEmpty: !rtsp.trim()
        });

        if (!autoStart) {
            console.log('[DEBUG useRtspToHls] autoStart is false, skipping startStream');
            return;
        }

        if (!rtsp.trim()) {
            console.log('[DEBUG useRtspToHls] RTSP path is empty, skipping startStream');
            return;
        }

        console.log('[DEBUG useRtspToHls] Calling startStream() with RTSP:', rtsp);
        startStream();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rtsp, autoStart, startStream]);

    // Cleanup khi unmount
    useEffect(() => {
        return () => {
            if (retryCtrlRef.current) retryCtrlRef.current.abort();
        };
    }, []);

    return [
        playlist,
        setRtsp,
        stopStream
    ];
};

